import { BrunaAvatar } from "@/core/components/bruna/bruna-avatar";
import { IconlyExternalLink } from "@/core/assets/icons";
import { Badge } from "@/core/components/ui/badge";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/core/components/ui/sidebar";
import { DS_COLOR, DS_TYPOGRAPHY } from "@/core/lib/ds-typography";
import { ROUTES } from "@/core/routes/constants";
import { EVENTS, useActionsTrack } from "@/core/services/menu-analytics";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isWosMenuItemActive, useWosMenu } from "../hooks/useWosMenu";

/**
 * Sidebar/Menu Item do Workbase DS (Figma 6051:2744): pílula de 36px, padding
 * 8/12, gap 8, ícone de 20px e rótulo Label/Xsmall em content-subtle. O item
 * ativo ganha a pílula bege (surface-accent-secondary-subtle-on-subtle) e o
 * texto content-common-active; hover usa surface-common-hover.
 * Recolhido, vira um botão de ícone de 36px centralizado.
 */
const ITEM_CLASS = [
  "h-9 gap-2 rounded-full px-3 py-2",
  "text-[color:var(--woba-color-content-subtle)]",
  "hover:bg-[var(--woba-color-surface-common-hover)] hover:text-[color:var(--woba-color-content-subtle)]",
  "active:bg-[var(--woba-color-surface-common-hover)]",
  "data-[active=true]:bg-[var(--woba-color-surface-accent-secondary-subtle-on-subtle)] data-[active=true]:text-[color:var(--woba-color-content-common-active)]",
  "data-[active=true]:hover:bg-[var(--woba-color-surface-accent-secondary-subtle-on-subtle)] data-[active=true]:hover:text-[color:var(--woba-color-content-common-active)]",
  "group-data-[collapsible=icon]:!size-9 group-data-[collapsible=icon]:!p-2",
  "[&_svg]:size-5 [&_svg]:shrink-0",
].join(" ");

const LABEL_STYLE = DS_TYPOGRAPHY.labelXsmall;

export function WosSidebarMenu() {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const { items } = useWosMenu();
  const { onActionClick } = useActionsTrack();
  const [translate] = useTranslation(["core"]);

  const location = useLocation();

  const isCollapsed = state === "collapsed";

  const onTrackAndCollapse = (tracker?: string) => {
    if (tracker) {
      onActionClick(tracker as keyof typeof EVENTS, "MENU");
    }
    if (isMobile) {
      toggleSidebar();
    }
  };

  const isActionCenterActive = location.pathname.startsWith(
    ROUTES.ACTION_CENTER,
  );

  const linkClass = `flex w-full items-center gap-2 ${
    isCollapsed ? "justify-center" : ""
  }`;

  return (
    <SidebarContent
      className={`gap-4 pb-4 pt-4 ${isCollapsed ? "px-1.5" : "px-4"}`}
    >
      <SidebarGroup className="p-0">
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActionCenterActive}
                tooltip={translate("app-menu-action-center") as string}
                data-testid="wos-menu-action-center"
                className={ITEM_CLASS}
              >
                <Link
                  to={ROUTES.ACTION_CENTER}
                  onClick={() => onTrackAndCollapse("MENU_ACTION_CENTER_CLICK")}
                  className={linkClass}
                >
                  <BrunaAvatar size={20} />
                  {!isCollapsed && (
                    <span className="flex-1 truncate" style={LABEL_STYLE}>
                      {translate("app-menu-action-center")}
                    </span>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {items.map((section) => (
        <SidebarGroup key={section.title} className="gap-1 p-0">
          {/* Header da Menu Section: 32px, padding 12/12/8, divisor no topo e
              rótulo Overline/Medium (maiúsculas, espaçamento de 1px). */}
          <SidebarGroupLabel
            className="relative h-8 rounded-none px-3 pb-2 pt-3"
            style={{
              ...DS_TYPOGRAPHY.overlineMedium,
              color: DS_COLOR.contentSubtle,
            }}
          >
            <span
              aria-hidden
              className="absolute inset-x-0 top-0 h-px"
              style={{ backgroundColor: DS_COLOR.strokeDefault }}
            />
            {section.title}
          </SidebarGroupLabel>
          {isCollapsed && (
            <span
              aria-hidden
              className="mx-1 h-px"
              style={{ backgroundColor: DS_COLOR.strokeDefault }}
            />
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {section.items.map((item) => {
                const isActive = isWosMenuItemActive(item, location.pathname);
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={item.title}
                      className={ITEM_CLASS}
                    >
                      {item.onSelect ? (
                        // Itens sem URL conhecida de antemão: o próprio item
                        // resolve navegação e tracking, não há rota ativa. O
                        // ícone de link externo só aparece quando o destino é
                        // outro sistema (`external`, design Figma).
                        <button
                          type="button"
                          onClick={() => {
                            item.onSelect?.();
                            if (isMobile) toggleSidebar();
                          }}
                          className={linkClass}
                        >
                          {item.icon()}
                          {!isCollapsed && (
                            <>
                              <span
                                className="flex-1 truncate text-left"
                                style={LABEL_STYLE}
                              >
                                {item.title}
                              </span>
                              {item.external && (
                                <span
                                  className="flex shrink-0 items-center [&_svg]:!size-4"
                                  style={{ width: 16, height: 16 }}
                                >
                                  <IconlyExternalLink size={16} />
                                </span>
                              )}
                            </>
                          )}
                        </button>
                      ) : item.external ? (
                        // Links externos: <a> nativo com target="_blank"
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => onTrackAndCollapse(item.tracker)}
                          className={linkClass}
                        >
                          {item.icon()}
                          {!isCollapsed && (
                            <>
                              <span
                                className="flex-1 truncate"
                                style={LABEL_STYLE}
                              >
                                {item.title}
                              </span>
                              <span
                                className="flex shrink-0 items-center [&_svg]:!size-4"
                                style={{ width: 16, height: 16 }}
                              >
                                <IconlyExternalLink size={16} />
                              </span>
                            </>
                          )}
                        </a>
                      ) : (
                        // Links internos: <Link> do React Router (gera <a href> correto)
                        <Link
                          to={item.link}
                          onClick={() => onTrackAndCollapse(item.tracker)}
                          className={linkClass}
                        >
                          {item.icon()}
                          {!isCollapsed && (
                            <span
                              className="flex-1 truncate"
                              style={LABEL_STYLE}
                            >
                              {item.title}
                            </span>
                          )}
                          {item.badge && (
                            <Badge className="ml-0 bg-primary-500 text-primary hover:bg-primary-500/80">
                              {item.badge}
                            </Badge>
                          )}
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </SidebarContent>
  );
}
